import React, { useEffect, useState } from "react";
import { floorPositionView } from "../../lib/api";
import { px, maskText } from "./floorUtil";

const sma = (c, n) => c.map((_, i) => (i < n - 1 ? null : c.slice(i - n + 1, i + 1).reduce((s, k) => s + k.close, 0) / n));

const Candles = ({ v }) => {
  const c = (v.candles || []).slice(-60);
  if (c.length < 2) return <p className="h-full flex items-center justify-center fc-label">candles unavailable for {v.symbol}</p>;
  const lv = [["ENTRY", v.entry, "#00F0FF"], ["TARGET", v.target, "#2CF6B3"], ["STOP", v.stop, "#FF3D68"]].filter((l) => l[1] != null && !Number.isNaN(Number(l[1])));
  const all = [...c.flatMap((k) => [k.high, k.low]), ...lv.map((l) => Number(l[1]))];
  const lo = Math.min(...all), hi = Math.max(...all), span = hi - lo || 1;
  const y = (p) => 96 - ((p - lo) / span) * 92, w = 100 / c.length;
  const line = (arr) => arr.map((s, i) => (s == null ? null : `${i * w + w / 2},${y(s)}`)).filter(Boolean).join(" ");
  const s20 = line(sma(c, 20)), s50 = line(sma(c, 50));
  return (
    <div className="relative h-full">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        {c.map((k, i) => { const up = k.close >= k.open, col = up ? "#2CF6B3" : "#FF3D68", x = i * w + w / 2;
          return (<g key={i}><line x1={x} x2={x} y1={y(k.high)} y2={y(k.low)} stroke={col} strokeWidth="1" vectorEffect="non-scaling-stroke" />
            <rect x={x - w * 0.34} width={w * 0.68} y={y(Math.max(k.open, k.close))} height={Math.max(0.4, Math.abs(y(k.open) - y(k.close)))} fill={col} /></g>); })}
        {s20 && <polyline points={s20} fill="none" stroke="#FACC15" strokeWidth="2" vectorEffect="non-scaling-stroke" />}
        {s50 && <polyline points={s50} fill="none" stroke="#A78BFA" strokeWidth="2" vectorEffect="non-scaling-stroke" />}
        {lv.map((l) => <line key={l[0]} x1="0" x2="100" y1={y(Number(l[1]))} y2={y(Number(l[1]))} stroke={l[2]} strokeWidth="1.5" strokeDasharray="6 4" vectorEffect="non-scaling-stroke" />)}
      </svg>
      {lv.map((l) => (
        <span key={l[0]} className="absolute right-[0.3em] -translate-y-1/2 fc-num font-bold text-[1em] px-[0.4em] rounded bg-black/80" style={{ top: `${y(Number(l[1]))}%`, color: l[2] }}>{l[0]} {px(l[1])}</span>
      ))}
      <div className="absolute top-[0.3em] left-[0.3em] flex gap-[0.6em] fc-num font-bold text-[1em]">
        {s20 && <span className="px-[0.4em] rounded bg-black/80 text-[#FACC15]">SMA20</span>}
        {s50 && <span className="px-[0.4em] rounded bg-black/80 text-[#A78BFA]">SMA50</span>}
      </div>
    </div>
  );
};

const FUND = [["Fwd P/E", "forward_pe"], ["P/E", "trailing_pe"], ["Profit mgn", "profit_margin", "%"], ["Op mgn", "operating_margin", "%"]];

const Chips = ({ v, pos, hidden }) => {
  const f = v?.fundamentals || {};
  const chips = [
    ["Side", String(pos.side || "").toUpperCase()], ["Venue", pos.venue], ["P&L", pos.pnl_pct != null ? `${Number(pos.pnl_pct) >= 0 ? "+" : ""}${Number(pos.pnl_pct).toFixed(2)}%` : null],
    ["Now", pos.current != null ? px(pos.current) : null],
    ...FUND.map(([l, k, u]) => [l, f[k] != null ? `${f[k]}${u || ""}` : null]),
    ["Insider", f.insider_flow && f.insider_flow !== "n/a" ? maskText(f.insider_flow, hidden) : null],
  ].filter((x) => x[1]);
  return (
    <div className="grid grid-rows-2 grid-flow-col auto-cols-[minmax(0,1fr)] gap-[0.4em] min-w-0" data-testid="tv-fundamental-chips">
      {chips.map(([l, val]) => (
        <div key={l} className="rounded-lg border border-cyan-300/25 bg-cyan-400/[0.05] px-[0.5em] py-[0.2em] min-w-0">
          <p className="fc-label truncate">{l}</p>
          <p className="fc-num font-bold text-[1.1em] text-cyan-50 truncate">{val}</p>
        </div>
      ))}
    </div>
  );
};

export const TvChart = ({ positions, hidden }) => {
  const open = [...(positions || [])].filter((p) => p.status === "open" && !/kalshi/i.test(p.venue || "")).sort((a, b) => Number(b.notional || 0) - Number(a.notional || 0));
  const [k, setK] = useState(0);
  useEffect(() => { const t = setInterval(() => setK((x) => x + 1), 20000); return () => clearInterval(t); }, []);
  const pos = open.length ? open[k % open.length] : null;
  const [v, setV] = useState(null);
  useEffect(() => {
    if (!pos?.id) return undefined;
    let dead = false;
    setV(null);
    const pull = () => floorPositionView(pos.id).then((r) => !dead && setV(r)).catch(() => {});
    pull();
    const t = setInterval(pull, 120000);
    return () => { dead = true; clearInterval(t); };
  }, [pos?.id]);
  const reasons = (v?.reasons || []).slice(0, 2);
  return (
    <section className="fc-panel fx-glow min-h-0 min-w-0 overflow-hidden p-[0.6em] flex flex-col gap-[0.5em]" data-testid="tv-technical">
      <div className="flex items-center gap-[0.5em] min-w-0 overflow-hidden">
        <p className="fc-label shrink-0">Technical</p>
        {open.map((p) => (
          <span key={p.id} className={`fc-num text-[1em] px-[0.5em] rounded-full border truncate ${p.id === pos?.id ? "border-[#00F0FF] text-[#030712] bg-[#00F0FF] font-bold" : "border-cyan-300/30 text-cyan-200/70"}`} data-testid={`tv-chart-tab-${p.symbol}`}>{p.symbol}</span>
        ))}
      </div>
      <div className="flex-1 min-h-0 rounded-xl border border-cyan-300/15 p-[0.3em]">
        {!pos ? <p className="h-full flex items-center justify-center fc-label">book is flat</p> : !v ? <p className="h-full flex items-center justify-center fc-label">loading {pos.symbol}</p> : <Candles v={v} />}
      </div>
      {pos && (
        <div className="space-y-[0.4em]">
          <p className="fc-label">Fundamental · why the floor holds it</p>
          <Chips v={v} pos={pos} hidden={hidden} />
          {reasons.map((r) => <p key={r.k} className="fc-num text-[1em] leading-snug text-cyan-50/85 line-clamp-1"><span className="text-[#00F0FF]">▸ </span>{maskText(r.text, hidden)}</p>)}
        </div>
      )}
    </section>
  );
};
